
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { HoverCard, HoverCardContent, HoverCardTrigger } from "@/components/ui/hover-card";
import { Target } from "lucide-react";
import { getProgressColor } from "../utils/badgeUtils";
import { getSelfRatingLabel, getManagerRatingLabel, formatDate } from "../utils/ratingUtils";
import { getStatusBadge, getPriorityBadge, getCategoryBadge } from "../utils/statusBadgeUtils";

interface Goal {
  id: string;
  goal_title: string;
  goal_description?: string;
  goal_category: string;
  goal_type: string;
  status: string;
  priority: string;
  target_value?: string;
  current_progress: number;
  target_date: string;
}

interface GoalsTableProps {
  goals: Goal[];
  goalSelfRatings: {[key: string]: string};
  goalManagerRatings: {[key: string]: string};
  goalComments: {[key: string]: string};
  goalManagerComments: {[key: string]: string};
  savedGoals: Set<string>;
  savedManagerGoals: Set<string>;
  isViewingOwnGoals: boolean;
  isSupervisorViewingTeamMember: boolean;
  goalAppraisals: {[key: string]: any};
  onSelfRatingChange: (goalId: string, value: string) => void;
  onManagerRatingChange: (goalId: string, value: string) => void;
  onEditSelfRating: (goal: Goal) => void;
  onEditManagerRating: (goal: Goal) => void;
}

const GoalsTable = ({
  goals,
  goalSelfRatings,
  goalManagerRatings,
  goalComments,
  goalManagerComments,
  savedGoals,
  savedManagerGoals,
  isViewingOwnGoals,
  isSupervisorViewingTeamMember,
  goalAppraisals,
  onSelfRatingChange,
  onManagerRatingChange,
  onEditSelfRating,
  onEditManagerRating
}: GoalsTableProps) => {
  const shouldShowSelfRatingDropdown = (goalId: string): boolean => {
    return isViewingOwnGoals && !savedGoals.has(goalId);
  };

  const shouldShowSelfRatingSaved = (goalId: string): boolean => {
    return savedGoals.has(goalId);
  };

  const shouldShowManagerRatingDropdown = (goalId: string): boolean => {
    const appraisal = goalAppraisals[goalId];
    if (!appraisal) return false;
    return isSupervisorViewingTeamMember && appraisal.self_rating !== null && appraisal.manager_rating === null;
  };

  const shouldShowManagerRatingSaved = (goalId: string): boolean => {
    return savedManagerGoals.has(goalId);
  };

  const handleSelfRatingSelect = (goalId: string, value: string) => {
    // Update the self rating
    onSelfRatingChange(goalId, value);
    
    // Find the goal and automatically trigger the edit self rating dialog
    const goal = goals.find(g => g.id === goalId);
    if (goal) {
      // Small delay to ensure the rating is updated before opening dialog
      setTimeout(() => {
        onEditSelfRating(goal);
      }, 100);
    }
  };

  const handleManagerRatingSelect = (goalId: string, value: string) => {
    // Update the manager rating
    onManagerRatingChange(goalId, value);
    
    // Find the goal and automatically trigger the edit manager rating dialog
    const goal = goals.find(g => g.id === goalId);
    if (goal) {
      // Small delay to ensure the rating is updated before opening dialog
      setTimeout(() => {
        onEditManagerRating(goal);
      }, 100);
    }
  };

  return (
    <Card className="bg-secondary/50 border-0">
      <CardHeader className="px-2 py-2">
        <div className="flex justify-between items-center">
          <div>
            <CardTitle className="text-white flex items-center gap-2">
              S.M.A.R.T. Goals ({goals.length})
            </CardTitle>
            <CardDescription className="text-red-500">
              Instructions: 1) Select rating from the dropdown menu for each goal, 2) then enter comments for the goal (mandatory).
            </CardDescription>
          </div>
        </div>
      </CardHeader>
      <CardContent className="px-0">
        {goals.length > 0 ? (
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow className="border-primary/20 hover:bg-secondary/30">
                  <TableHead className="text-primary text-[9.5px]">Goal Title</TableHead>
                  <TableHead className="text-primary text-[9.5px]">Category</TableHead>
                  <TableHead className="text-primary text-[9.5px]">Type</TableHead>
                  <TableHead className="text-primary text-[9.5px]">Status</TableHead>
                  <TableHead className="text-primary text-[9.5px]">Priority</TableHead>
                  <TableHead className="text-primary text-[9.5px]">Target Value</TableHead>
                  <TableHead className="text-primary text-[9.5px]">Progress</TableHead>
                  <TableHead className="text-primary text-[9.5px]">Target Date</TableHead>
                  <TableHead className="text-primary text-[9.5px]">Self Rating</TableHead>
                  <TableHead className="text-primary text-[9.5px]">Manager Rating</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {goals.map(goal => {
                  const statusBadge = getStatusBadge(goal.status);
                  const priorityBadge = getPriorityBadge(goal.priority);
                  const categoryBadge = getCategoryBadge(goal.goal_category);
                  
                  return (
                    <TableRow key={goal.id} className="border-primary/10 hover:bg-secondary/20">
                      <TableCell className="text-white text-[9.5px]">
                        <div className="flex flex-col text-[9.5px]">
                          <span className="font-medium">{goal.goal_title}</span>
                          {goal.goal_description && (
                            <span className="text-[9.5px] text-gray-400 mt-1 line-clamp-2">
                              {goal.goal_description}
                            </span>
                          )}
                        </div>
                      </TableCell>
                      <TableCell>
                        <Badge className={categoryBadge.color}>
                          {categoryBadge.label}
                        </Badge>
                      </TableCell>
                      <TableCell>
                        <Badge className="bg-indigo-500/20 text-indigo-300 text-[9.5px] border-indigo-500/50">
                          {goal.goal_type === 'performance' ? 'Performance' : 'Behavioral'}
                        </Badge>
                      </TableCell>
                      <TableCell>
                        <Badge className={statusBadge.color}>
                          {statusBadge.label}
                        </Badge>
                      </TableCell>
                      <TableCell>
                        <Badge className={priorityBadge.color}>
                          {priorityBadge.label}
                        </Badge>
                      </TableCell>
                      <TableCell className="text-gray-300 text-[9.5px]">
                        {goal.target_value || '-'}
                      </TableCell>
                      <TableCell>
                        <div className="flex items-center gap-2 text-[9.5px]">
                          <span className={`text-[9.5px] ${getProgressColor(goal.current_progress)}`}>
                            {goal.current_progress}%
                          </span>
                          <div className="w-16 h-2 bg-gray-700 rounded-full overflow-hidden text-[9.5px]">
                            <div 
                              className={`h-full transition-all duration-300 text-[9.5px] ${
                                goal.current_progress >= 90 ? 'bg-green-400' : 
                                goal.current_progress >= 70 ? 'bg-yellow-400' : 
                                goal.current_progress >= 50 ? 'bg-orange-400' : 'bg-red-400'
                              }`} 
                              style={{ width: `${Math.min(goal.current_progress, 100)}%` }} 
                            />
                          </div>
                        </div>
                      </TableCell>
                      <TableCell className="text-gray-300 text-[9.5px]">
                        <div className="flex items-center gap-1 text-[9.5px]">
                          {formatDate(goal.target_date)}
                        </div>
                      </TableCell>
                      <TableCell className="text-[9.5px]">
                        <div className="flex items-center justify-between">
                          {shouldShowSelfRatingSaved(goal.id) ? (
                            <HoverCard>
                              <HoverCardTrigger asChild>
                                <span className="text-white text-[9.5px] cursor-pointer hover:text-primary transition-colors">
                                  {getSelfRatingLabel(goalSelfRatings[goal.id] || "")}
                                </span>
                              </HoverCardTrigger>
                              <HoverCardContent side="top" className="bg-black text-white p-2 rounded max-w-xs border-none">
                                <p className="text-sm">
                                  {goalComments[goal.id] || "No comments available"}
                                </p>
                              </HoverCardContent>
                            </HoverCard>
                          ) : shouldShowSelfRatingDropdown(goal.id) ? (
                            <Select 
                              value={goalSelfRatings[goal.id] || ""} 
                              onValueChange={value => handleSelfRatingSelect(goal.id, value)}
                            >
                              <SelectTrigger className="w-[80px] h-7 text-[9.5px] bg-secondary border-primary/20 text-white">
                                <SelectValue placeholder="Select" />
                              </SelectTrigger>
                              <SelectContent className="bg-white border-primary/20 min-w-[200px]">
                                <SelectItem value="far-exceeds-expectations" className="text-black text-[9.5px]">
                                  Far Exceeds Expectations
                                </SelectItem>
                                <SelectItem value="exceeds-expectations" className="text-black text-[9.5px]">
                                  Exceeds Expectations
                                </SelectItem>
                                <SelectItem value="meets-expectations" className="text-black text-[9.5px]">
                                  Meets Expectations
                                </SelectItem>
                                <SelectItem value="below-expectations" className="text-black text-[9.5px]">
                                  Below Expectations
                                </SelectItem>
                                <SelectItem value="far-below-expectations" className="text-black text-[9.5px]">
                                  Far Below Expectations
                                </SelectItem>
                              </SelectContent>
                            </Select>
                          ) : (
                            <span className="text-gray-400 text-[9.5px]">TBD</span>
                          )}
                        </div>
                      </TableCell>
                      <TableCell className="text-[9.5px]">
                        <div className="flex items-center justify-between">
                          {shouldShowManagerRatingSaved(goal.id) ? (
                            <HoverCard>
                              <HoverCardTrigger asChild>
                                <span className="text-white text-[9.5px] cursor-pointer hover:text-primary transition-colors">
                                  {getManagerRatingLabel(goalManagerRatings[goal.id] || "")}
                                </span>
                              </HoverCardTrigger>
                              <HoverCardContent side="top" className="bg-black text-white p-2 rounded max-w-xs border-none">
                                <p className="text-sm">
                                  {goalManagerComments[goal.id] || "No comments available"}
                                </p>
                              </HoverCardContent>
                            </HoverCard>
                          ) : shouldShowManagerRatingDropdown(goal.id) ? (
                            <Select 
                              value={goalManagerRatings[goal.id] || ""} 
                              onValueChange={value => handleManagerRatingSelect(goal.id, value)}
                            >
                              <SelectTrigger className="w-[80px] h-7 text-[9.5px] bg-secondary border-primary/20 text-white">
                                <SelectValue placeholder="Select" />
                              </SelectTrigger>
                              <SelectContent className="bg-white border-primary/20 min-w-[200px]">
                                <SelectItem value="far-exceeds-expectations" className="text-black text-[9.5px]">
                                  Far Exceeds Expectations
                                </SelectItem>
                                <SelectItem value="exceeds-expectations" className="text-black text-[9.5px]">
                                  Exceeds Expectations
                                </SelectItem>
                                <SelectItem value="meets-expectations" className="text-black text-[9.5px]">
                                  Meets Expectations
                                </SelectItem>
                                <SelectItem value="below-expectations" className="text-black text-[9.5px]">
                                  Below Expectations
                                </SelectItem>
                                <SelectItem value="far-below-expectations" className="text-black text-[9.5px]">
                                  Far Below Expectations
                                </SelectItem>
                              </SelectContent>
                            </Select>
                          ) : (
                            <span className="text-gray-400 text-[9.5px]">TBD</span>
                          )}
                        </div>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </div>
        ) : (
          <div className="text-center py-12">
            <Target className="h-12 w-12 text-gray-400 mx-auto mb-4" />
            <h3 className="text-white text-[9.5px] font-medium mb-2">No performance goals yet</h3>
            <p className="text-gray-400 text-[9.5px] mb-4">Goals will appear here when available for appraisal.</p>
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export default GoalsTable;
